'use client';
import { DateTime } from 'luxon';
import React from 'react';

import ListPage from '@/components/page-components/list-page/list-page';
import { createListCardArray } from '@/helpers/list-page/list-page-helpers';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { IMeetingplacePageHomeMinifiedExtendedAttributeFilters } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageTenantList } from '@/lib/fragments/meetingPlacePageTenantList';
import {
  useGetActiveOffers,
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import {
  pageIsTenantEntertainment,
  pageIsTenantFoodBeverage,
  pageIsTenantShop,
} from '@/helpers/list-page/type-guards';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
// import { PageContext } from '../../../../page-context.interface';

interface MeetingPlacePageTenantListProps {
  contentfulMeetingPlacePageTenantList: IMeetingPlacePageTenantList;
  contentfulMeetingPlacePageHome: IMeetingplacePageHomeMinifiedExtendedAttributeFilters;
}
const MeetingPlacePageTenantList = ({
  metaData,
  node_locale,
  tenantListData,
  initialPage,
}: any) => {
  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();
  const config = useTenantConfig();
  const activeOffers = useGetActiveOffers();

  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';

  const page = tenantListData?.meetingPlacePageTenantList;
  const homepage = tenantListData?.meetingPlacePageHomeCollection;

  // const offersForTenants = pageContext?.offersForTenants
  //   ? pageContext?.offersForTenants
  //   : {
  //       offerListPage: { offerItems: [] },
  //       occationListPage: { occasions: [] },
  //     };

  const tenants = page?.tenantsCollection?.items ?? [];

  // Logic to check if offer exists for a tenant in offer listing and occassion
  tenants?.forEach((tenant: any) => {
    const hasOffer = activeOffers?.find(
      (offer: any) => offer?.tenant?.sys?.id == tenant?.sys?.id,
    );
    tenant.hasOffer = hasOffer ? true : false;
    const offerCampaignNames = [
      ...new Set(
        activeOffers
          ?.filter((offer: any) => offer?.tenant?.sys?.id == tenant?.sys?.id)
          .flatMap((o: any) => o?.tags ?? [])
          .filter(Boolean),
      ),
    ] as string[];
    if (offerCampaignNames.length > 0) {
      tenant.offerCampaignNames = offerCampaignNames;
    }
  });

  const shopPagesInTenantList = tenants
    ?.filter(Boolean)
    ?.filter((t: any) => pageIsTenantShop(t));
  const foodBeveragePagesInTenantList = tenants
    ?.filter(Boolean)
    ?.filter((t: any) => pageIsTenantFoodBeverage(t));
  const entertainmentPagesInTenantList = tenants
    ?.filter(Boolean)
    ?.filter((t: any) => pageIsTenantEntertainment(t));

  const shopListSlugFromSubDirectory = getUrlSubdirectory(
    urlSubDirectorydictionary,
    node_locale,
    'ContentfulMeetingPlacePageTenantShopItem',
  );
  const foofBevListSlugFromSubDirectory = getUrlSubdirectory(
    urlSubDirectorydictionary,
    node_locale,
    'ContentfulMeetingPlacePageTenantFoodBevItem',
  );
  const entertainmentListSlugFromSubDirectory = getUrlSubdirectory(
    urlSubDirectorydictionary,
    node_locale,
    'ContentfulMeetingPlacePageTenantEntertainmentItem',
  );

  const shopsTenantCardList = createListCardArray(
    shopPagesInTenantList,
    undefined,
    undefined,
    shopListSlugFromSubDirectory,
    node_locale,
    prefix,
  );
  const foodBeverageTenantCardList = createListCardArray(
    foodBeveragePagesInTenantList,
    undefined,
    undefined,
    foofBevListSlugFromSubDirectory,
    node_locale,
    prefix,
  );
  const entertainmentTenantCardList = createListCardArray(
    entertainmentPagesInTenantList,
    undefined,
    undefined,
    entertainmentListSlugFromSubDirectory,
    node_locale,
    prefix,
  );

  const tenantCardList = [
    ...(shopsTenantCardList && shopsTenantCardList),
    ...(foodBeverageTenantCardList && foodBeverageTenantCardList),
    ...(entertainmentTenantCardList && entertainmentTenantCardList),
  ];

  const isWebView = useGetMobileAppWebViewHeader();

  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
      />

      <ListPage
        cardList={tenantCardList}
        pageName={page?.name}
        pageSlug={page?.slug}
        node_locale={node_locale}
        // location={location}
        breadcrumbs={[page?.name]}
        homeText={homepage?.breadcrumbHomeText ?? ''}
        tileColClass="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        listType="tenant"
        attributeFilters={homepage?.meetingPlace.attributeFilters}
        isWebView={isWebView}
        initialPage={initialPage ?? 1}
      />
    </>
  );
};

export default MeetingPlacePageTenantList;
